import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AudioPlayerProps } from "~/interface/audioplayer/audioplayer";
import { getSession, setSession } from "~/lib/helperFuncation/sessionStorageService";
import { AUDIO_PLAYER } from "~/utils/constant";

export type MediaTypeControl = "podcast" | "music";
interface AudioState {
  currentAudio: AudioPlayerProps | null;
  isPlaying: boolean;
  isAudioPlay: boolean;
  audioQueue: AudioPlayerProps[] | null;
  currentTime: number;
  duration: number; // Add duration here (in seconds)
  playbackSpeed: number; // Add playback speed here (e.g., 1.0)
  desktopAccordionShow: boolean;
  isRepeat: boolean; // Optional: global repeat state
  isShuffle: boolean; // Optional: global shuffle state
  progress: number;
  isPlayAllAudio?: boolean;
  isMobileDrawerOpen: boolean;
  showDeleteConfirmation?: boolean;
  media_type_control: MediaTypeControl;
  is_annkut_audio_playing?: boolean;
}

const initialState: AudioState = {
  currentAudio: getSession(AUDIO_PLAYER.CURRENT_AUDIO) || null,
  isPlaying: false,
  isAudioPlay: getSession(AUDIO_PLAYER.IS_AUDIO_PLAYING) || false,
  audioQueue: getSession(AUDIO_PLAYER.AUDIO_QUEUE) || null,
  currentTime: getSession(AUDIO_PLAYER.CURRENT_TIME) || 0,
  duration: 0, // default duration zero
  playbackSpeed: 1, // default playback speed 1x
  desktopAccordionShow: false,
  isRepeat: false, // default no repeat
  isShuffle: false, // default no shuffle
  progress: 0,
  isPlayAllAudio: getSession(AUDIO_PLAYER.PLAY_ALL_AUDIO) || false,
  isMobileDrawerOpen: false,
  showDeleteConfirmation: false,
  media_type_control: getSession(AUDIO_PLAYER.MEDIA_TYPE_CONTROL) || "music",
  is_annkut_audio_playing: false,
};

export const audioSlice = createSlice({
  name: "audio",
  initialState,
  reducers: {
    addToCurrentAudio: (state, action: PayloadAction<AudioPlayerProps | null>) => {
      state.currentAudio = action.payload;
      const isMediaTypeControlProvided = action.payload?.media_type_control;
      state.media_type_control = isMediaTypeControlProvided ? isMediaTypeControlProvided : "music";
      setSession(AUDIO_PLAYER.MEDIA_TYPE_CONTROL, state.media_type_control);
      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUDIO_PLAYER.CURRENT_AUDIO, JSON.stringify(action.payload));
      }
    },
    changeIsPlayingState: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
      if (!action.payload) {
        state.is_annkut_audio_playing = false;
      }

      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUDIO_PLAYER.IS_PLAY, JSON.stringify(action.payload));
      }
    },
    changeIsAudioPlayState: (state, action: PayloadAction<boolean>) => {
      state.isAudioPlay = action.payload;
      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUDIO_PLAYER.IS_AUDIO_PLAYING, JSON.stringify(action.payload));
      }
    },
    addAudioQueue: (state, action: PayloadAction<AudioPlayerProps[] | null>) => {
      state.audioQueue = action.payload;
      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUDIO_PLAYER.AUDIO_QUEUE, JSON.stringify(action.payload));
      }
    },
    removeQueue: (state) => {
      state.audioQueue = null;
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(AUDIO_PLAYER.AUDIO_QUEUE);
      }
    },
    setCurrentAudioTime: (state, action: PayloadAction<number>) => {
      state.currentTime = action.payload;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
    setPlaybackSpeed: (state, action: PayloadAction<number>) => {
      state.playbackSpeed = action.payload;
    },
    setRepeat: (state, action: PayloadAction<boolean>) => {
      state.isRepeat = action.payload;
    },
    setShuffle: (state, action: PayloadAction<boolean>) => {
      state.isShuffle = action.payload;
    },
    changeDesktopAccordionStatus: (state, action: PayloadAction<boolean>) => {
      state.desktopAccordionShow = action.payload;
    },
    changeMobileDrawerStatus: (state, action: PayloadAction<boolean>) => {
      state.isMobileDrawerOpen = action.payload;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setPlayAllAudio: (state, action: PayloadAction<boolean>) => {
      state.isPlayAllAudio = action.payload;
      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUDIO_PLAYER.PLAY_ALL_AUDIO, JSON.stringify(action.payload));
      }
    },
    setShowDeleteConfirmation: (state, action: PayloadAction<boolean>) => {
      state.showDeleteConfirmation = action.payload;
    },
    setMediaTypeControl: (state, action: PayloadAction<MediaTypeControl>) => {
      const payload = action.payload || "music";
      state.media_type_control = payload;
      setSession(AUDIO_PLAYER.MEDIA_TYPE_CONTROL, payload);
    },
    setIsAnnakutAudioPlaying: (state, action: PayloadAction<boolean>) => {
      state.is_annkut_audio_playing = action.payload;
    },
  },
});

export const {
  addToCurrentAudio,
  changeIsPlayingState,
  changeIsAudioPlayState,
  addAudioQueue,
  removeQueue,
  setCurrentAudioTime,
  setDuration,
  setPlaybackSpeed,
  setRepeat,
  setShuffle,
  changeDesktopAccordionStatus,
  setProgress,
  setPlayAllAudio,
  changeMobileDrawerStatus,
  setShowDeleteConfirmation,
  setMediaTypeControl,
  setIsAnnakutAudioPlaying,
} = audioSlice.actions;

export default audioSlice.reducer;
